import type { BlocklistImportPreviewRow } from "@unbox/api";
import { AlertTriangle } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useBlocklistImportCommit } from "@/api/blocklist";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { ACTION_LABELS } from "@/lib/assurance/bulk-action-errors";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { error, success } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";

// Every selectable row starts checked — importing a CSV assumes the user
// wants to block everyone in it, so they handpick exceptions rather than
// handpick inclusions. not_found/already_blocked stay permanently unchecked
// and their checkbox is disabled. "exempt" is selectable too (the user can
// still choose to override an exemption) but does NOT start pre-checked —
// unlike a plain unmatched row, an exemption is an explicit "don't block
// this person" signal, so importing one has to be a deliberate choice.
const isSelectable = (row: BlocklistImportPreviewRow) =>
  row.status === "matched" || row.status === "unmatched" || row.status === "exempt";
const defaultChecked = (row: BlocklistImportPreviewRow) => row.status === "matched" || row.status === "unmatched";

interface BlocklistImportReviewDrawerProps {
  rows: BlocklistImportPreviewRow[];
  onOpenChange: (open: boolean) => void;
}

export function BlocklistImportReviewDrawer({ rows, onOpenChange }: BlocklistImportReviewDrawerProps) {
  const commit = useBlocklistImportCommit();
  const { startTracking } = useBulkActionContext();
  const [view, setView] = useState<"list" | "guard">("list");
  const [checkedByOpenId, setCheckedByOpenId] = useState<Map<string, boolean>>(
    () => new Map(rows.filter((r) => r.creatorOpenId).map((r) => [r.creatorOpenId as string, defaultChecked(r)])),
  );

  const selectedCount = useMemo(() => {
    let selected = 0;
    for (const row of rows) {
      if (!row.creatorOpenId) continue;
      const checked = checkedByOpenId.get(row.creatorOpenId) ?? false;
      if (!checked) continue;
      selected += 1;
    }
    return selected;
  }, [rows, checkedByOpenId]);

  const selectableOpenIds = useMemo(
    () => rows.filter((r) => r.creatorOpenId && isSelectable(r)).map((r) => r.creatorOpenId as string),
    [rows],
  );
  const allSelected = selectableOpenIds.length > 0 && selectableOpenIds.every((id) => checkedByOpenId.get(id));
  const someSelected = selectableOpenIds.some((id) => checkedByOpenId.get(id));

  const toggleRow = (creatorOpenId: string, checked: boolean) => {
    setCheckedByOpenId((prev) => new Map(prev).set(creatorOpenId, checked));
  };

  const toggleAll = (checked: boolean) => {
    setCheckedByOpenId((prev) => {
      const next = new Map(prev);
      for (const id of selectableOpenIds) next.set(id, checked);
      return next;
    });
  };

  // Any dismissal attempt (X, click-outside, Escape, Cancel) is intercepted:
  // from the list it shows the guard; from the guard it acts as "Keep reviewing".
  // A committed import closes via handleImport's own onOpenChange(false), which
  // bypasses this and never trips the guard (there's nothing left to lose).
  const requestClose = () => {
    setView((current) => (current === "list" ? "guard" : "list"));
  };

  const handleImport = async () => {
    // Every row committed here came through the CSV import flow, so it's
    // always origin "csv" — whether a row was pre-flagged by an active
    // rule or the user manually checked it in this review screen only
    // affects the default checkbox state, not the final "Added via" value.
    const toCommit = rows
      .filter((r) => r.creatorOpenId && checkedByOpenId.get(r.creatorOpenId))
      .map((r) => r.creatorOpenId as string);

    try {
      const result = await commit.mutateAsync(toCommit);

      if (result.async) {
        startTracking(result.jobId, "import_blocklist", null);
        onOpenChange(false);
        return;
      }

      if (result.failed > 0) {
        error(ACTION_LABELS.import_blocklist.partialFailure);
      } else {
        success(
          <>
            Done. <strong className="font-semibold text-brand">{result.blocked}</strong> creators imported and{" "}
            <strong className="font-semibold text-brand">Blocked</strong>.
          </>,
        );
      }
      onOpenChange(false);
    } catch {
      // A failed request (network drop, timeout, etc.) previously left the drawer
      // stuck with no feedback at all — show an explicit error and leave the drawer
      // open so the user can retry instead of silently doing nothing.
      error("Something went wrong importing creators — please try again.");
    }
  };

  return (
    <SlideoutMenu isOpen onOpenChange={(open) => !open && requestClose()} isDismissable>
      <SlideoutMenu.Header onClose={requestClose}>
        <h2 className="text-lg font-semibold text-primary">Review import</h2>
      </SlideoutMenu.Header>

      {view === "guard" ? (
        <>
          <SlideoutMenu.Content className="items-center justify-center gap-4 px-6 md:px-6">
            <div className="flex w-full max-w-sm flex-col items-center gap-3 rounded-xl bg-secondary px-6 py-8 text-center">
              <FeaturedIcon icon={AlertTriangle} color="warning" theme="light" size="md" />
              <h3 className="text-md font-semibold text-primary">Unsaved import</h3>
              <p className="text-sm text-tertiary">
                You haven't finished reviewing these users. If you leave now, this import won't be saved.
              </p>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-row items-center justify-between gap-3">
            <Button color="link-gray" onClick={() => onOpenChange(false)}>
              Discard import
            </Button>
            <div className="flex flex-row gap-3">
              <Button color="secondary" onClick={() => setView("list")}>
                Keep reviewing
              </Button>
              <Button
                color="primary"
                isLoading={commit.isPending}
                isDisabled={selectedCount === 0}
                onClick={handleImport}
              >
                Import
              </Button>
            </div>
          </SlideoutMenu.Footer>
        </>
      ) : (
        <>
          <SlideoutMenu.Content className="flex flex-col gap-0 overflow-hidden px-0 md:px-0">
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-secondary bg-primary px-6 py-3 text-sm text-tertiary">
              <Checkbox
                size="sm"
                isSelected={allSelected}
                isIndeterminate={someSelected && !allSelected}
                isDisabled={selectableOpenIds.length === 0}
                onChange={toggleAll}
                aria-label="Select all"
              />
              <span>{selectedCount.toLocaleString()} selected</span>
            </div>

            <div className="flex flex-1 flex-col overflow-y-auto">
              {rows.map((row) => {
                const checked = row.creatorOpenId ? (checkedByOpenId.get(row.creatorOpenId) ?? false) : false;
                const disabled = !isSelectable(row);
                const isError = row.status === "not_found"; // red is reserved for this alone
                return (
                  <div
                    key={`${row.line}-${row.handle}`}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => !disabled && row.creatorOpenId && toggleRow(row.creatorOpenId, !checked)}
                    onKeyDown={(e) => {
                      if (disabled || !row.creatorOpenId) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleRow(row.creatorOpenId, !checked);
                      }
                    }}
                    className={cx(
                      "flex items-center gap-3 border-b border-secondary px-6 py-3",
                      isError && "bg-error-primary/5",
                      !disabled && "cursor-pointer hover:bg-primary_hover",
                    )}
                  >
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: wrapper only stops click propagation to the row; the nested Checkbox handles its own keyboard interaction */}
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: same reason — no keyboard event needed, the wrapper isn't itself interactive */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="sm"
                        isSelected={checked}
                        isDisabled={disabled}
                        onChange={(next) => row.creatorOpenId && toggleRow(row.creatorOpenId, next)}
                      />
                    </div>
                    <Avatar
                      size="sm"
                      src={row.avatar}
                      alt={row.creatorName ?? row.handle}
                      initials={(row.creatorName ?? row.handle).charAt(0).toUpperCase()}
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-primary">{row.creatorName ?? row.handle}</span>
                      {row.username && <span className="truncate text-xs text-tertiary">@{row.username}</span>}
                    </div>
                    {row.reason && (
                      <span className={cx("text-xs", isError ? "text-error-primary" : "text-tertiary")}>
                        {row.reason}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-row justify-between gap-3">
            <Button color="secondary" onClick={requestClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              isLoading={commit.isPending}
              isDisabled={selectedCount === 0}
              onClick={handleImport}
            >
              Import ({selectedCount.toLocaleString()})
            </Button>
          </SlideoutMenu.Footer>
        </>
      )}
    </SlideoutMenu>
  );
}
